const FALLBACK_ZONES = [
    'UTC',
    'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris',
    'Europe/Brussels', 'Europe/Amsterdam', 'Europe/Berlin', 'Europe/Copenhagen', 'Europe/Oslo',
    'Europe/Stockholm', 'Europe/Rome', 'Europe/Prague', 'Europe/Warsaw', 'Europe/Budapest',
    'Europe/Athens', 'Europe/Helsinki', 'Europe/Bucharest', 'Europe/Kyiv', 'Europe/Istanbul',
    'Europe/Moscow',
    'Atlantic/Reykjavik', 'Atlantic/Azores', 'Atlantic/Canary',
    'America/St_Johns', 'America/Halifax', 'America/New_York', 'America/Toronto',
    'America/Chicago', 'America/Mexico_City', 'America/Denver', 'America/Phoenix',
    'America/Los_Angeles', 'America/Vancouver', 'America/Anchorage', 'Pacific/Honolulu',
    'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Sao_Paulo',
    'America/Argentina/Buenos_Aires',
    'Africa/Casablanca', 'Africa/Lagos', 'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Nairobi',
    'Asia/Jerusalem', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
    'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Shanghai',
    'Asia/Manila', 'Asia/Seoul', 'Asia/Tokyo',
    'Australia/Perth', 'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Sydney',
    'Pacific/Auckland', 'Pacific/Fiji'
];

const partsCache = new Map();

const pad = (value) => String(value).padStart(2, '0');

const partsFormatter = (timeZone) => {
    if (!partsCache.has(timeZone)) {
        partsCache.set(timeZone, new Intl.DateTimeFormat('en-GB', {
            timeZone,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }));
    }
    return partsCache.get(timeZone);
};

export const isValidZone = (timeZone) => {
    if (!timeZone) return false;
    try {
        partsFormatter(timeZone).format(new Date());
        return true;
    } catch {
        partsCache.delete(timeZone);
        return false;
    }
};

export const detectTimeZone = () => {
    try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return isValidZone(zone) ? zone : 'UTC';
    } catch {
        return 'UTC';
    }
};

export const zonedParts = (value, timeZone) => {
    const date = value instanceof Date ? value : new Date(value);
    const parts = partsFormatter(timeZone).formatToParts(date);

    const read = (type) => {
        const found = parts.find(part => part.type === type);
        return found ? Number(found.value) : 0;
    };

    const hour = read('hour');

    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: hour === 24 ? 0 : hour,
        minute: read('minute'),
        second: read('second')
    };
};

const offsetMs = (date, timeZone) => {
    const p = zonedParts(date, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - (Math.floor(date.getTime() / 1000) * 1000);
};

export const zonedToUtc = (dateStr, timeStr, timeZone) => {
    const [year, month, day] = String(dateStr || '').split('-').map(Number);
    const [hour, minute] = String(timeStr || '00:00').split(':').map(Number);

    if (!year || !month || !day) return null;

    const naive = Date.UTC(year, month - 1, day, hour || 0, minute || 0, 0);

    let stamp = naive - offsetMs(new Date(naive), timeZone);
    stamp = naive - offsetMs(new Date(stamp), timeZone);

    return new Date(stamp);
};

export const dayKey = (value, timeZone) => {
    const p = zonedParts(value, timeZone);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

export const timeLabel = (value, timeZone) => {
    const p = zonedParts(value, timeZone);
    return `${pad(p.hour)}:${pad(p.minute)}`;
};

export const toInputValues = (value, timeZone) => {
    const p = zonedParts(value, timeZone);
    return {
        date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
        time: `${pad(p.hour)}:${pad(p.minute)}`
    };
};

export const offsetLabel = (timeZone, at = new Date()) => {
    const minutes = Math.round(offsetMs(at, timeZone) / 60000);
    if (minutes === 0) return 'UTC';

    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);

    return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

export const listTimeZones = () => {
    let zones = FALLBACK_ZONES;

    try {
        if (typeof Intl.supportedValuesOf === 'function') {
            const supported = Intl.supportedValuesOf('timeZone');
            if (Array.isArray(supported) && supported.length) zones = ['UTC', ...supported];
        }
    } catch {
        zones = FALLBACK_ZONES;
    }

    return [...new Set(zones)].filter(isValidZone);
};

export const monthGrid = (year, month) => {
    const first = new Date(Date.UTC(year, month, 1));
    const lead = (first.getUTCDay() + 6) % 7;

    const cells = [];
    const cursor = new Date(Date.UTC(year, month, 1 - lead));

    for (let i = 0; i < 42; i += 1) {
        cells.push({
            key: `${cursor.getUTCFullYear()}-${pad(cursor.getUTCMonth() + 1)}-${pad(cursor.getUTCDate())}`,
            day: cursor.getUTCDate(),
            inMonth: cursor.getUTCMonth() === ((month % 12) + 12) % 12
        });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    while (cells.length > 35 && !cells.slice(35).some(cell => cell.inMonth)) {
        cells.length = 35;
    }

    return cells;
};

export const addDaysToKey = (key, days) => {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day + days));
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

export const readableDay = (key) => {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
    });
};

export const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
